import { computed, makeObservable, observable, action } from 'mobx';
import { getRollingLimits, putRollingLimits } from 'src_server/trpc/types/responsibleGambling';
import { RollingNetDepositLimitItemState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/responsibleGambling/rollingNetDepositLimit/RollingNetDepositLimitItem';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { ResourceCustomer, RollingNetDepositLimitDataType } from 'src/domains/players/state/UsersState';
import { Amount } from 'src_common/common/amount/Amount';
import {
    RollingPayloadListType,
    preparePayload,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/RollingNetDepositLimit.state';
import { CreateAccountStep } from 'src/domains/players/webview/components/SignUp';
import { CurrencyType, isCurrencyCode } from 'src_common/common/amount/website-money/currency';
import { Common } from 'src/domains/common/Common';

export type InfoMessageType = 'success' | 'error' | 'empty' | 'belowOne';

interface ConvertedLimitsType {
    [key: string]: Amount | undefined;
}
interface ChekIsDepositLimitAlreadySetType {
    newValueWhenDiffer: Record<number, Amount | undefined>;
}

export class SignupRollingNetDepositLimitState {
    private readonly limitsType: 'calendar' | 'rolling';
    public readonly daily: RollingNetDepositLimitItemState;
    public readonly weekly: RollingNetDepositLimitItemState;
    public readonly monthly: RollingNetDepositLimitItemState;

    private readonly rollingNetDepositLimitData: ResourceCustomer<RollingNetDepositLimitDataType>;

    @observable public infoMessage: InfoMessageType | null = null;

    public constructor(
        private readonly common: Common,
        private readonly createAccountStep: CreateAccountStep
    ) {
        makeObservable(this);
        this.limitsType =
            ConfigComponents.get(this.common).config.depositLimitsType === 'calendarType' ? 'calendar' : 'rolling';

        this.rollingNetDepositLimitData = new ResourceCustomer(
            'rollingNetDepositLimitData',
            common.session,
            async () => {
                const payload = await this.common.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query();

                if (payload.responseStatus === 'success') {
                    return new Map(
                        payload.data
                            .sort((a, b) => a.windowLengthInDays - b.windowLengthInDays)
                            .map((activeObj) => [activeObj.windowLengthInDays, activeObj])
                    );
                } else {
                    return new Map();
                }
            },
            { allowEphemeral: true }
        );

        this.daily = new RollingNetDepositLimitItemState(
            ConfigComponents.get(this.common),
            this.selectedCurrency,
            this.clearInfoMessage,
            () => this.getRollingLimits().get(1)
        );

        this.weekly = new RollingNetDepositLimitItemState(
            ConfigComponents.get(this.common),
            this.selectedCurrency,
            this.clearInfoMessage,
            () => this.getRollingLimits().get(7)
        );

        this.monthly = new RollingNetDepositLimitItemState(
            ConfigComponents.get(this.common),
            this.selectedCurrency,
            this.clearInfoMessage,
            () => this.getRollingLimits().get(30)
        );
    }
    private clearInfoMessage = (): void => {
        this.infoMessage = null;
    };

    private clearInputs = (): void => {
        this.daily.resetValue();
        this.weekly.resetValue();
        this.monthly.resetValue();
    };

    private getRollingLimits = (): Map<number, getRollingLimits.LimitsObjectSchemaType> => {
        const data = this.rollingNetDepositLimitData.valueReady;
        if (data === null) {
            return new Map();
        }
        return data;
    };

    @computed public get isLoading(): boolean {
        const depositsModel = this.rollingNetDepositLimitData.get();

        return depositsModel.type === 'loading';
    }

    @computed public get isErrorInput(): boolean {
        return this.daily.isError || this.weekly.isError || this.monthly.isError;
    }

    private getActualLimits = (): ConvertedLimitsType => {
        const getValue = (period: RollingNetDepositLimitItemState): Amount | undefined => {
            const shortenValue = period.limitAmount.result.value;
            return shortenValue.type === 'ok' ? shortenValue.data : undefined;
        };

        return {
            dailyValue: getValue(this.daily),
            weeklyValue: getValue(this.weekly),
            monthlyValue: getValue(this.monthly),
        };
    };

    private getPreviousLimits = (): ConvertedLimitsType => {
        const getPreviousLimit = (period: RollingNetDepositLimitItemState): Amount | undefined => {
            const limit = period.getLimits()?.activeLimitValue;
            return limit === undefined ? undefined : ConfigComponents.get(this.common).precision.newFromOld(limit);
        };

        return {
            previousDaily: getPreviousLimit(this.daily),
            previousWeekly: getPreviousLimit(this.weekly),
            previousMonthly: getPreviousLimit(this.monthly),
        };
    };

    private checkIsDepositLimitAlreadySet = (): ChekIsDepositLimitAlreadySetType => {
        const { dailyValue, weeklyValue, monthlyValue } = this.getActualLimits();
        const { previousDaily, previousWeekly, previousMonthly } = this.getPreviousLimits();

        const hasValueChanged = (newValue: Amount | undefined, oldValue: Amount | undefined): boolean =>
            newValue !== undefined && oldValue?.value !== newValue.value;

        return {
            newValueWhenDiffer: {
                1: hasValueChanged(dailyValue, previousDaily) ? dailyValue : undefined,
                7: hasValueChanged(weeklyValue, previousWeekly) ? weeklyValue : undefined,
                30: hasValueChanged(monthlyValue, previousMonthly) ? monthlyValue : undefined,
            },
        };
    };

    private checkIsLessThanOne(limits: ConvertedLimitsType): boolean {
        const limit = new Amount('1');
        const values = [limits.dailyValue, limits.weeklyValue, limits.monthlyValue];

        return values.some((value) => value !== undefined && value.isLessThan(limit));
    }

    private async updateRollingLimits(
        payload: RollingPayloadListType,
        currentLimits: getRollingLimits.SuccessResponseType
    ): Promise<putRollingLimits.ResponseType> {
        const preparedPayload = preparePayload(payload, currentLimits);
        const response = await this.common.trpcClient.client.responsibleGamblingRouter.putRollingLimits.mutate({
            payload: {
                source: 'registration',
                list: preparedPayload,
            },
        });
        return response;
    }

    private setTriggersWarningEmail = (limitValueMax: number | null, pendingLimitValue: number): boolean => {
        return pendingLimitValue === limitValueMax ? true : false;
    };

    private async updateLimits(): Promise<void> {
        const daysArray = [1, 7, 30];
        const preparedPayload: putRollingLimits.LimitObjectType[] = [];

        daysArray.forEach((days) => {
            const newValue = this.checkIsDepositLimitAlreadySet().newValueWhenDiffer[days];
            if (newValue !== undefined) {
                const previousLimitData = this.getRollingLimits().get(days);
                const pendingLimitValue = parseInt(newValue.multiply(100).value, 10);
                const triggersWarningEmail = this.setTriggersWarningEmail(
                    previousLimitData?.limitValueMax ?? null,
                    pendingLimitValue
                );

                preparedPayload.push({
                    windowLengthInDays: days,
                    pendingLimitValue,
                    limitValueMax: previousLimitData?.limitValueMax ?? null,
                    triggersWarningEmail,
                    hardLimit: previousLimitData?.hardLimit ?? false,
                    limitType: this.limitsType,
                });
            }
        });

        const currentLimits = await this.common.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query();

        if (currentLimits.responseStatus === 'success') {
            const response = await this.updateRollingLimits(preparedPayload, currentLimits.data);
            this.infoMessage = response.responseStatus === 'success' ? 'success' : 'error';

            if (response.responseStatus === 'success') {
                this.rollingNetDepositLimitData.refresh();
            }
        } else {
            this.infoMessage = 'error';
        }
    }

    public setAtLeastOneLimit = (): boolean => {
        const { dailyValue, weeklyValue, monthlyValue } = this.getActualLimits();

        if (dailyValue === undefined && weeklyValue === undefined && monthlyValue === undefined) {
            return false;
        }
        return true;
    };

    @action public onSubmit = async (): Promise<void> => {
        const { dailyValue, weeklyValue, monthlyValue } = this.getActualLimits();

        if (dailyValue === undefined && weeklyValue === undefined && monthlyValue === undefined) {
            this.clearInputs();
            this.infoMessage = 'empty';
            return;
        }

        if (this.checkIsLessThanOne({ dailyValue, weeklyValue, monthlyValue })) {
            this.clearInputs();
            this.infoMessage = 'belowOne';
            return;
        }

        try {
            await this.updateLimits();
        } catch (error) {
            console.log(error);
            this.infoMessage = 'error';
        }
    };

    @computed public get selectedCurrency(): CurrencyType {
        const currency = this.createAccountStep.yourDetailsState.currency.currentValue;

        if (isCurrencyCode(currency)) {
            return currency;
        }

        return 'GBP';
    }
}
